import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DESIGNS = [
  { id: "purple-turf", label: "Purple Turf", bg: "bg-gradient-to-br from-purple-700 to-purple-950", emblemText: "THE PURPLE TURF · MILTON, MA" },
  { id: "colonels", label: "Colonels Pride", bg: "bg-gradient-to-br from-zinc-800 to-zinc-950", emblemText: "CURRY COLONELS FOOTBALL" },
  { id: "game-day", label: "Game Day", bg: "bg-gradient-to-br from-purple-900 via-zinc-900 to-zinc-950", emblemText: "GAME DAY · FALL 2026" },
];

function PostcardVisual({ design, message, sender, small }) {
  const d = DESIGNS.find((x) => x.id === design) || DESIGNS[0];
  return (
    <div className={`${d.bg} rounded-2xl border border-white/10 p-6 ${small ? "min-h-[180px]" : "min-h-[240px]"} flex flex-col justify-between relative overflow-hidden`}>
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 38px, rgba(255,255,255,0.6) 38px, rgba(255,255,255,0.6) 40px)" }} />
      <div className="relative flex items-center justify-between">
        <span className="font-display text-lg tracking-wide text-white/90">CHASE @ CURRY</span>
        <span className="w-10 h-12 rounded-sm border-2 border-dashed border-white/40 flex items-center justify-center text-[8px] text-white/50 text-center leading-tight px-1">CURRY 2026</span>
      </div>
      <p className={`relative font-sub text-white/95 ${small ? "text-sm" : "text-base"} italic leading-relaxed my-4 line-clamp-4`}>
        "{message || "Your message here..."}"
      </p>
      <div className="relative flex items-center justify-between">
        <span className="font-sub text-xs text-white/70">— {sender || "Your name"}</span>
        <span className="font-sub text-[10px] tracking-[0.2em] text-white/50 uppercase">{d.emblemText}</span>
      </div>
    </div>
  );
}

export default function Postcards() {
  const [cards, setCards] = useState([]);
  const [design, setDesign] = useState("purple-turf");
  const [sender, setSender] = useState("");
  const [message, setMessage] = useState("");

  const load = () => axios.get(`${API}/postcards`).then((r) => setCards(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!sender.trim() || !message.trim()) return toast.error("Add your name and a message");
    await axios.post(`${API}/postcards`, { sender, message, design });
    toast.success("Postcard sent to Chase's wall! 💜");
    setSender("");
    setMessage("");
    load();
  };

  const printCard = () => {
    if (!message.trim()) return toast.error("Write your message first");
    window.print();
  };

  return (
    <section id="postcards" className="py-24 md:py-32 max-w-7xl mx-auto px-4 sm:px-6">
      <p className="font-sub text-xs tracking-[0.2em] uppercase font-bold text-purple-400 mb-3">Snail Mail, Reinvented</p>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase mb-4">
        Send A <span className="text-purple-400">Postcard</span>
      </h2>
      <p className="font-sub text-base md:text-lg text-zinc-400 max-w-2xl mb-12">
        Design a Curry-themed postcard. Send it electronically to Chase's wall, or print it
        and drop it in the mail to campus.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-5">
          <div className="flex flex-wrap gap-3">
            {DESIGNS.map((d) => (
              <button
                key={d.id}
                data-testid={`postcard-design-${d.id}`}
                onClick={() => setDesign(d.id)}
                className={`font-sub text-sm font-semibold px-5 py-2 rounded-full border transition-colors duration-200 ${design === d.id ? "bg-purple-600 border-purple-600 text-white" : "border-white/10 text-zinc-400 hover:text-white hover:border-purple-400"}`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <Input data-testid="postcard-sender" placeholder="Your name" value={sender} onChange={(e) => setSender(e.target.value)} className="bg-zinc-900 border-white/10" />
          <Textarea data-testid="postcard-message" placeholder="Write your message to Chase..." rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="bg-zinc-900 border-white/10" />
          <div className="flex flex-wrap gap-4">
            <Button data-testid="postcard-send-btn" onClick={send} className="bg-purple-600 hover:bg-purple-500 font-sub font-semibold rounded-full px-8 py-6">
              <Send className="w-4 h-4 mr-2" /> Send Electronically
            </Button>
            <Button data-testid="postcard-print-btn" onClick={printCard} variant="outline" className="border-white/20 hover:border-purple-400 bg-transparent text-white hover:bg-transparent hover:text-purple-300 font-sub font-semibold rounded-full px-8 py-6">
              <Printer className="w-4 h-4 mr-2" /> Print for Mailing
            </Button>
          </div>
        </div>
        <div>
          <p className="font-sub text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">Live Preview</p>
          <PostcardVisual design={design} message={message} sender={sender} />
        </div>
      </div>

      {cards.length > 0 && (
        <div className="mt-20">
          <h3 className="font-sub font-bold text-xl text-white mb-8 flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-400" /> Postcards Received ({cards.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="postcard-gallery">
            {cards.map((c) => (
              <PostcardVisual key={c.id} design={c.design} message={c.message} sender={c.sender} small />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
